import { FC, useEffect, useMemo, useRef, useState } from 'react'

import { useNavigate, useSearchParams } from 'react-router-dom'

import { useOktaAuth } from '@okta/okta-react'



import { Alert, Box, SpaceBetween } from '@cloudscape-design/components'

import Wizard, { WizardProps } from '@cloudscape-design/components/wizard'



import Step1 from '../../../components/AccountCreation/Steps/Step1'

import Step2 from '../../../components/AccountCreation/Steps/Step2'

import Tags from '../../../components/AccountCreation/Steps/Tags'

import Review from '../../../components/AccountCreation/Steps/Review'



import { useAccountCreationFormStore } from '../../../stores/accounts/newAccountCreationFormStore'

import { AccountRequest, type StgFollowUpRequest } from '../../../types/account'

import useCreateNewAccount from '../../../hooks/mutate/useCreateNewAccount'

import useSubmitStgFollowUp from '../../../hooks/mutate/useSubmitStgFollowUp'



import { useGenericTagsManagementFormStore } from '../../../stores/tags/genericTagsManagementFormStore'

import useCreateNewTags from '../../../hooks/mutate/useCreateNewTags'

import { TagsRequest } from '../../../types/tags'
import { buildAwsFundingTagRequest } from '../../../helper/awsFundingApproval'

import { v4 as uuidv4 } from 'uuid'

import {

	buildStgOnboardingAccountFormValues,

	buildStgOnboardingTagFormValues

} from '../../../helper/stgOnboardingPrefill'
import { getAccountPath } from '../../../helper/accountRoutes'
import useSingleAccount from '../../../hooks/query/useSingleAccount'

import LoadingSpinner from '../../../components/LoadingSpinner'



const i18nStrings: WizardProps.I18nStrings = {

	stepNumberLabel: (stepNumber: number) => `Step ${stepNumber}`,

	collapsedStepsLabel: (stepNumber: number, stepsCount: number) =>

		`Step ${stepNumber} of ${stepsCount}`,

	skipToButtonLabel: (step: { title: string }, _stepNumber: number) =>

		`Skip to ${step.title}`,

	navigationAriaLabel: 'Steps',

	cancelButton: 'Cancel',

	previousButton: 'Previous',

	nextButton: 'Next',

	submitButton: 'Submit',

	optional: 'optional'

}



const wizardSteps: Array<WizardProps.Step> = [

	{ title: 'Account Types', content: <Step1 /> },

	{ title: 'Account Details', content: <Step2 /> },

	{ title: 'Tags Details', content: <Tags /> },

	{ title: 'Review', content: <Review /> }

]



const convertFormValuesToAccountRequestDetails = (

	formValues: Record<string, unknown>

): Record<string, unknown> => {

	const details: Record<string, unknown> = {

		accountWorkloadType: formValues.accountWorkloadType || 'Non-EKS'

	}



	const accountTypes = Array.isArray(formValues.accountTypes)

		? formValues.accountTypes

		: []



	accountTypes.forEach((accountType: { value: string }) => {

		const rawSSO = formValues[`${accountType.value}SSOUserNames`]

		const SSOUserNames = Array.isArray(rawSSO)

			? rawSSO

					.filter(

						(entry: { userName?: string }) =>

							entry && String(entry.userName ?? '').trim()

					)

					.map((entry: { userName: string; displayName?: string }) => ({

						userName: String(entry.userName).trim(),

						displayName: String(entry.displayName ?? '').trim()

					}))

			: rawSSO

		details[accountType.value] = {

			distributionList: formValues[`${accountType.value}DistributionList`],

			monthlyBudgetCap: formValues[`${accountType.value}MonthlyBudgetCap`],

			platformOwner: formValues[`${accountType.value}platformOwner`],

			SSOUserNames

		}

	})



	return details

}



const AccountCreationForm: FC = () => {

	const { authState } = useOktaAuth()

	const thisUserEmail = authState?.idToken?.claims.email as string



	const navigate = useNavigate()

	const [searchParams] = useSearchParams()

	const fromAccountId = (searchParams.get('fromAccount') ?? '').trim()

	const isStgOnboarding = searchParams.get('stg') === '1' || searchParams.get('stg') === 'true'

	const stgOnboardingEnabled = isStgOnboarding && fromAccountId.length > 0

	const stgPrefillDoneRef = useRef(false)



	const { data: stgSourceAccount, isSuccess: stgSourceLoaded, isLoading: stgSourceLoading } =

		useSingleAccount(fromAccountId, true, { enabled: stgOnboardingEnabled })



	const accountCreationFormStore = useAccountCreationFormStore()

	const tagCreationFormStore = useGenericTagsManagementFormStore()



	const [stgPrefillComplete, setStgPrefillComplete] = useState(!stgOnboardingEnabled)

	const tagsStepIndex = 2



	const stgCanPrefill = useMemo(() => {

		if (!stgOnboardingEnabled || !stgSourceAccount || stgSourceAccount.status === 'ERROR') {

			return false

		}

		return Boolean(buildStgOnboardingAccountFormValues(stgSourceAccount))

	}, [stgOnboardingEnabled, stgSourceAccount])



	useEffect(() => {

		if (!stgOnboardingEnabled) {

			setStgPrefillComplete(true)

			return

		}

		if (!stgSourceLoaded || !stgSourceAccount) return

		if (stgSourceAccount.status === 'ERROR') {

			setStgPrefillComplete(true)

			return

		}

		if (stgPrefillDoneRef.current) return

		const accPart = buildStgOnboardingAccountFormValues(stgSourceAccount)

		if (accPart) {

			accountCreationFormStore.actions.replaceFormValues(accPart)

			tagCreationFormStore.actions.replaceFormValues(

				buildStgOnboardingTagFormValues(stgSourceAccount.tags)

			)

			accountCreationFormStore.actions.setStgFollowUpReadOnly(true)

			accountCreationFormStore.actions.setCurrentStep(tagsStepIndex)

		}

		stgPrefillDoneRef.current = true

		setStgPrefillComplete(true)

	}, [stgOnboardingEnabled, stgSourceLoaded, stgSourceAccount, accountCreationFormStore.actions, tagCreationFormStore.actions, tagsStepIndex])



	const { mutate: createNewAccount, isLoading: isLoadingAccount } =

		useCreateNewAccount()

	const { mutate: createTags, isLoading: isLoadingTags } = useCreateNewTags()

	const { mutate: submitStgFollowUp, isLoading: isSubmittingStgFollowUp } =

		useSubmitStgFollowUp()



	const handleSubmit = () => {

		const { stgFollowUpReadOnly, formValues } = accountCreationFormStore



		if (

			stgFollowUpReadOnly &&

			stgOnboardingEnabled &&

			fromAccountId &&

			stgSourceAccount &&

			stgSourceAccount.status !== 'ERROR'

		) {

			const stgPayload: StgFollowUpRequest = {

				action: 'stg_onboard',

				accountRequestID: fromAccountId,

				requestorID: thisUserEmail,

				details: convertFormValuesToAccountRequestDetails(formValues)

			}

			submitStgFollowUp(stgPayload, {

				onSuccess: () => {

					accountCreationFormStore.actions.clearFormValues()

					tagCreationFormStore.actions.clearFormValues()

					navigate(getAccountPath(fromAccountId))

				}

			})

			return

		}



		let tagsID = ''

		const accountTypes = formValues.accountTypes || []

		accountTypes.forEach(() => {

			tagsID = tagsID === '' ? uuidv4() : tagsID + ',' + uuidv4()

		})



		const accountRequest: AccountRequest = {

			requestorID: thisUserEmail,

			action: 'CREATE',

			status: 'PENDING_APPROVAL',

			tagsID: tagsID,

			details: convertFormValuesToAccountRequestDetails(formValues)

		}



		const tagRequest: TagsRequest = buildAwsFundingTagRequest({
			requestorID: thisUserEmail,
			tagsID: tagsID,
			fundingChoice: 'own',
			details: tagCreationFormStore.formValues
		})



		createTags(tagRequest, {

			onSuccess: () => {

				createNewAccount(accountRequest, {

					onSuccess: () => {

						accountCreationFormStore.actions.clearFormValues()

						tagCreationFormStore.actions.clearFormValues()

						navigate('/accounts')

					}

				})

			}

		})

	}



	const handleCancel = () => {

		accountCreationFormStore.actions.clearFormValues()

		tagCreationFormStore.actions.clearFormValues()

		accountCreationFormStore.actions.setCurrentStep(0)

		if (stgOnboardingEnabled && fromAccountId) {

			navigate(getAccountPath(fromAccountId))

		} else {

			navigate(-1)

		}

	}



	const handleWizardNavigate = ({

		detail

	}: {

		detail: WizardProps.NavigateDetail

	}) => {

		if (detail.reason === 'next') {

			if (accountCreationFormStore.currentStep === 1) {

				if (!accountCreationFormStore.isFormValid) {

					return

				}

			}

			if (accountCreationFormStore.currentStep === tagsStepIndex) {

				if (

					!accountCreationFormStore.stgFollowUpReadOnly &&

					!tagCreationFormStore.isFormValid

				) {

					return

				}

			}

		}



		accountCreationFormStore.actions.setCurrentStep(detail.requestedStepIndex)

	}



	const isNextDisabled =

		(accountCreationFormStore.currentStep === 1 && !accountCreationFormStore.isFormValid) ||

		(accountCreationFormStore.currentStep === tagsStepIndex &&

			!accountCreationFormStore.stgFollowUpReadOnly &&

			!tagCreationFormStore.isFormValid)



	if (stgOnboardingEnabled && (!stgPrefillComplete || stgSourceLoading)) {

		return <LoadingSpinner />

	}



	return (

		<div style={{ position: 'relative' }}>

			<SpaceBetween size="l" direction="vertical">

				{stgOnboardingEnabled && !stgCanPrefill && stgSourceLoaded && (

					<Alert type="warning" header="Could not pre-fill from the selected account">

						<Box variant="p">

							STG pre-fill is only available when the source account type is

							Non-Production. You can still create a new request from scratch.

						</Box>

					</Alert>

				)}

				<Wizard

					i18nStrings={i18nStrings}

					steps={wizardSteps}

					activeStepIndex={accountCreationFormStore.currentStep}

					onNavigate={handleWizardNavigate}

					onSubmit={handleSubmit}

					isLoadingNextStep={

						isLoadingAccount || isLoadingTags || isSubmittingStgFollowUp

					}

					onCancel={handleCancel}

				/>

			</SpaceBetween>

			{isNextDisabled && (

				<style>{`

					[class*="wizard"] button[class*="primary"]:not([class*="secondary"]) {

						opacity: 0.5 !important;

						cursor: not-allowed !important;

						pointer-events: none !important;

					}

					.awsui_box button:nth-child(2) {

						opacity: 0.5 !important;

						cursor: not-allowed !important;

						pointer-events: none !important;

					}

				`}</style>

			)}

		</div>

	)

}



export default AccountCreationForm

